import {
    getCanonicalSearchUrl,
    pruneObject,
} from '@eventbrite/redux-destination';
import { $FixMe } from '@eventbrite/ts-utils';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import queryString from 'query-string';
import { RouteComponentProps } from 'react-router-dom';
import { formatUrl } from 'url-lib';
import {
    END_DATE,
    EVENT_PAGE_PARAM,
    SEARCH_RELEVANT_QUERY_PARAMS,
    START_DATE,
} from '../../constants';
import { FOLLOWED_ORGS_FILTER_VALUE } from '../../constants/filters';
import { EventSearch } from '../../types/index';

/*
 *
 * @param path {string} -- canonical path
 * @param eventSearch {object} -- state.search.eventSearch
 *
 * Func creates the appropriate URL object with approved and formatted query params. Assumes incoming
 * path is canonical already.
 *
 */
export const constructObjectForUrl = (
    path: string,
    eventSearch: EventSearch,
    currentPage?: number,
) => {
    const {
        dateRange,
        bbox,
        currencies: cur,
        languages: lang,
        special = [],
        organizationsOr: organization,
        hash,
    } = eventSearch;
    const query = pruneObject({
        [EVENT_PAGE_PARAM]: currentPage,
        [START_DATE]: dateRange.from,
        [END_DATE]: dateRange.to,
        bbox,
        cur,
        lang,
        followed_orgs: special.includes(FOLLOWED_ORGS_FILTER_VALUE) ? 1 : '',
        organization,
        hash,
    });

    return {
        query,
        pathname: path,
    };
};

/*
@param eventSearch {object} - takes current application state
@param locationSlug {string} - location slug like ca--san-francisco
Returns a formatted URL with appropriate query params and updated event page value
*/
export const constructNormalizedSearchUrl = (
    page = 1,
    eventSearch: EventSearch,
    locationSlug = '',
) => {
    // Emojis and weird characters can generate problems
    const path = getCanonicalSearchUrl({ slug: locationSlug, ...eventSearch });
    const formattedPath = decodeURI(path);

    const { query: constructedQuery } = constructObjectForUrl(
        path,
        eventSearch,
        page,
    );

    return getPathWithSanitizedQueryParams(formattedPath, constructedQuery);
};

/*
@param eventSearch {object} - takes current application state
@param locationSlug {string} - location slug like ca--san-francisco
Returns a formatted URL with appropriate query params and updated event page value
*/
export const constructSearchUrl = (
    page = 1,
    eventSearch: EventSearch,
    locationSlug = '',
    location: RouteComponentProps['location'],
) => {
    // Emojis and weird characters can generate problems
    const path = getCanonicalSearchUrl({ slug: locationSlug, ...eventSearch });
    const formattedPath = decodeURI(path);

    const currentQueryParams = getCurrentExternalQueryParams(location);
    const { query: constructedQuery } = constructObjectForUrl(
        path,
        eventSearch,
        page,
    );

    return (
        formatUrl(formattedPath, {
            ...currentQueryParams,
            ...constructedQuery,
        }) + location.hash
    );
};

// Return the url used as key in the previousUrlContext state
export function getCurrentNormalizedUrl(
    history: RouteComponentProps['history'],
) {
    const queryParams = queryString.parse(history.location.search);
    return getPathWithSanitizedQueryParams(
        history.location.pathname,
        queryParams,
    );
}

// Return query params that do not affect search query
export const getCurrentExternalQueryParams = (
    location: RouteComponentProps['location'],
    whiteListedQueryParams = SEARCH_RELEVANT_QUERY_PARAMS,
) => {
    const queryParams = queryString.parse(location.search);
    return omit(queryParams, whiteListedQueryParams);
};
/*
@param path - String
@param queryParams - Object
Returns url formatted path with sanitized queryParams included
*/
export const getPathWithSanitizedQueryParams = (
    path: string,
    queryParams: Record<string, unknown>,
    whiteListedQueryParams = SEARCH_RELEVANT_QUERY_PARAMS,
) => {
    const normalizedQueryParams = {
        ...queryParams,
        // Remove page=1, since it's the same as not having it
        page: Number(queryParams.page) === 1 ? undefined : queryParams.page,
    };
    /* Query params we care about that will influence the search value */
    const sanitizedQuery: $FixMe = pick(
        normalizedQueryParams,
        whiteListedQueryParams,
    );

    return formatUrl(path, sanitizedQuery);
};
